<template>
  <select-field ref="ACTIVITE_SELECT"
                :value=selectedItem
                :items=listItems
                :disabled=readOnly
                :editFieldInfo=editFieldInfo
                :required=required
                :selectText=selectText
                :showMissing=showMissing
                :hideLabel=hideLabel
                :contextId=contextId
                :notOrdered=notOrdered
                @updated=setUpdated
                @focus=onFocus
                @error=setError>
  </select-field>
</template>

<script lang="ts">
  import { defineComponent, type PropType } from "vue";
  import { FieldError, FieldStatus, EditFieldType, FieldInfo, EditFieldTheme } from '@/framework/base/components/FieldInfo'
  import SelectField, { SELECT_VALUE, SELECT_TEXT } from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Activite from '@/extend/classes/Activite';
  import Ressource from '@/framework/ressource/classes/Ressource';
  import ListData, { ListFilter, ColumnFilter } from '@/extend/classes/ListData';
  import PlageCreneau from '@/extend/classes/PlageCreneau';

  export default defineComponent({
    components: {
      SelectField
    },

    props: {
      value: {
        type: Number,
        required: true
      },
      contextId: {
        type: Number,
        required: true
      },
      toggleReset: {
        type: Boolean,
      },
      readOnly: {
        type: Boolean,
      },
      required: {
        type: Boolean,
      },
      showMissing: {
        type: Boolean,
      },
      selectText: {
        type: String,
      },
      theme: {
        type: Object as PropType<EditFieldTheme>
      },
      ressource: {
        type: Object as PropType<Ressource>,
        required: true
      },
      hideLabel: {
        type: Boolean,
      },
      filters: {
        type: Object as PropType<ColumnFilter[]>,
      },
      plageCreneau: {
        type: Object as PropType<PlageCreneau>,
      },
      activiteId: Number,
    },

    created (): void {
      this.initValue = this.value ? this.value : 0;
      this.status.objId = this.contextId;
      this.reset = !this.toggleReset;
    },
    data() {
      const listActivite: Activite[] = [];
      return {
        initValue: 0,
        settedValue: 0,
        saving: false,
        status: new FieldStatus(false, false, 0),
        reset: true,
        listActivite,
        lastContextId: 0,
        labelValue: '',
        listUpdated: false,
        notOrdered: false
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SELECT;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      items (): Activite [] {
        if (this.reset != this.toggleReset) {
          this.loadData()
        }
        return this.listActivite;
      },

      listItems (): Item[] {
        const res: Item[] = [];
        this.items.forEach((activite) => {
          let value = this.getDisplay(activite);
          if (value == null) {
            value = activite.label;
          }
          return res.push(new Item(activite.id, value ? value : ''));
        });
        return res;
      },

      selectedItem (): string {
        if (this.lastContextId != this.contextId || this.listUpdated || this.value != this.settedValue) {
          const listItems = this.listItems;
          if (listItems.length > 0) {
            this.selectedItemsValue();
            if (this.value > 0) {
              this.selectedItemLabel();
            } else if (listItems.length == 1) {
              this.setUpdated(listItems[0]);
            } else if (listItems.length == 2) {
              if (listItems[0].key == 0) {
                this.setUpdated(listItems[1]);
              } else if (listItems[1].key == 0) {
                this.setUpdated(listItems[0]);
              } else {
                this.setUpdated(this.defaultItem());
              }
            } else if (listItems.length > 2) {
              this.setUpdated(this.defaultItem());
            }
          }
        }
        return this.labelValue;
      }
    },

    methods: {
      loadData() {
        this.reset = this.toggleReset;
        const listFilters = new ListFilter();
        listFilters.columnsFilter = this.filters;
        Activite.list(listFilters)
          .then((data: ListData) => {
            this.listActivite = data.reccords as Activite[];
            this.listUpdated = true;
          }).catch(() => {
            //Catch empty list
          });
      },

      selectedItemsValue() {
        this.initValue = this.value ? this.value : 0;
        this.settedValue = this.value ? this.value : 0;
        this.status.objId = this.contextId;
        this.lastContextId = this.contextId;
        this.listUpdated = false;
      },

      selectedItemLabel() {
        const item = this.listItems.find(a => a.key == this.value);
        if (item) {
          this.labelValue = item.title;
        }
      },

      // eslint-disable-next-line
      getDisplay(item: Activite): string | null {
        return null
      },

      defaultItem(): Item {
        return new Item(SELECT_VALUE, SELECT_TEXT);
      },

      save (): void {
        this.saving = true;
        this.initValue = this.value;
        this.status.clear(this.contextId);
        this.updateStatus(this.status.updated, this.status.error);
      },

      setUpdated (item: Item): void {
        if (!this.saving) {
          this.settedValue = item.key;
          this.labelValue = item.title;
          this.itemChanged(item.key);
          const updated = (item.key != this.initValue) && (item.key != SELECT_VALUE || this.initValue != 0)
          this.updateStatus(updated, this.status.error);
        }
        this.saving = false
      },

      onFocus () {
        this.saving = false;
        if (this.items.length == 0) {
          this.reset = !this.reset
        }
      },

      setError (value: FieldError): void {
        this.updateStatus(this.status.updated, value.error);
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id < 0 && id == SELECT_VALUE ? 0 : id);
      },

      updateStatus (updated: boolean, error: boolean): void {
        if (this.status.updated != updated || this.status.error != error) {
          this.status.updated = updated;
          this.status.error = error;
          this.$emit('status', this.status);
        }
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.ACTIVITE_SELECT.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.ACTIVITE_SELECT.touch();
      }
    }
  })
</script>
